import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Box, Button, Input, FormControl, FormLabel, VStack, Text, useToast, Select, Flex, IconButton } from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { checkInfluencerPhone } from '../../services/firebaseService';
import { RecaptchaVerifier } from 'firebase/auth';
import { countryOptions } from 'types';
import { formatPhoneNumber } from 'utils/authUtils';
import { usePhoneVerification } from '../../hooks/usePhoneVerification';
import { DisplayHeading } from '../../components/ui';
import { ff } from '../../theme';

declare global {
  interface Window {
    grecaptcha: any;
    recaptchaVerifier: RecaptchaVerifier;
  }
}

const ReturningUser: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>('+1'); // Default country code
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState<boolean>(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { sendVerificationCode } = usePhoneVerification();

  const postSignInPageInfo = useLocation().state;

  useEffect(() => {
    const recaptchaContainer = document.getElementById('recaptcha-container');
    if (recaptchaContainer) {
      recaptchaContainer.style.visibility = 'hidden';
      setRecaptchaLoaded(true);
    }

    return () => {
      if (recaptchaContainer) {
        recaptchaContainer.style.visibility = 'hidden';
      }
    };
  }, []);

  const handleSendCode = async () => {
    setIsLoading(true);
    try {
      const fullPhoneNumber = formatPhoneNumber(phoneNumber, countryCode);
      const isValid = await checkInfluencerPhone(fullPhoneNumber);
      if (!isValid) {
        toast({
          title: "Error",
          description: "We do not have an account for this phone number.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        await sendVerificationCode(fullPhoneNumber, recaptchaLoaded, postSignInPageInfo);
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      // Error handling is already done in the hook
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minH="100svh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      bg="white"
      px={{ base: '20px', md: 6 }}
      pt={{ base: 4, md: 8 }}
      pb={{ base: 6, md: 10 }}
    >
      <Box width="100%" maxW="440px">
        <IconButton
          aria-label="Back"
          icon={<FiArrowLeft size={20} />}
          onClick={() => navigate('/login')}
          variant="ghost"
          color={ff.ink}
          minW="44px"
          h="44px"
          ml="-10px"
        />
      </Box>

      <VStack
        spacing={6}
        align="stretch"
        width="100%"
        maxW="440px"
        flex="1"
        justifyContent="space-between"
        pt={6}
      >
        {/* Top section */}
        <VStack spacing={8} align="stretch">
          <VStack spacing={2} align="stretch">
            <DisplayHeading size="32px">Welcome back</DisplayHeading>
            <Text fontSize="15px" color={ff.text2}>
              Glad to see you again! Enter your phone number and we'll text you a code.
            </Text>
          </VStack>
          <FormControl id="phoneNumber">
            <FormLabel>Phone number</FormLabel>
            <Flex gap={2}>
              <Select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                width="31%"
                aria-label="Country code"
              >
                {countryOptions.map(option => (
                  <option key={option.code} value={option.code}>
                    {option.code}
                  </option>
                ))}
              </Select>
              <Input
                type="tel"
                placeholder="Phone number"
                value={phoneNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)}
              />
            </Flex>
          </FormControl>
        </VStack>

        {/* Bottom section */}
        <VStack spacing={4} align="stretch">
          <Button
            variant="lime"
            width="full"
            onClick={handleSendCode}
            isLoading={isLoading}
          >
            Send code
          </Button>
          <Text fontSize="15px" color={ff.text2} textAlign="center">
            Don't have an account?{' '}
            <Link to="/new-signup" style={{ color: ff.ink, fontWeight: 600, textDecoration: 'underline' }}>Register now</Link>
          </Text>
        </VStack>
      </VStack>
    </Box>
  );
};

export default ReturningUser;
